import { useCallback, useMemo } from "react";
import {
  ExploreColumnType,
  ExploreGroupByColumnType,
  ExploreAggColumnType,
  exploreColumnTypeToLabel,
  exploreGroupByColumnTypes,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import {
  AllowedAggOrderBy,
  AllowedInteractionOrderBy,
  exploreOrderByTypeToLabel,
} from "./ExploreDisplayOptions.model";
import {
  conversationsNotAllowedGroupBy,
  defaultConversationsAggColumns,
  defaultConversationsColumns,
  defaultTicketsAggColumns,
  defaultTicketsColumns,
  mandatoryColumns,
  orderableColumns,
  orderableMetricAggColumns,
  ticketsNotAllowedGroupBy,
} from "./ExploreDisplayOptions.consts";
import {
  visibleConversationAggColumns,
  visibleConversationColumns,
  visibleTicketAggColumns,
  visibleTicketColumns,
} from "@/v2/entities/explore/columns/useExploreColumns";
import { DisplayOptions } from "./ExploreDisplayOptions.model";
import { useExploreCustomFields } from "../useExploreCustomFields";
import { useExploreConversationCustomFieldsForView } from "../useExploreConversationCustomFieldsForView";

export function useExploreDisplayOptions({
  value,
  onChange,
  source,
}: {
  value: DisplayOptions;
  onChange: (value: DisplayOptions) => void;
  source: "tickets" | "conversations";
}) {
  const customFields = useExploreCustomFields();
  const conversationCustomFieldsForView =
    useExploreConversationCustomFieldsForView();

  const availableGroupByOptions = useMemo(() => {
    const notAllowed =
      source === "conversations"
        ? conversationsNotAllowedGroupBy
        : ticketsNotAllowedGroupBy;

    return exploreGroupByColumnTypes.filter(
      (option) => !notAllowed.includes(option)
    );
  }, [source]);

  const availableColumns = useMemo(() => {
    const ticketDynamicColumns = customFields.map(
      (customField) => customField.id as ExploreColumnType
    );
    const conversationDynamicColumns = conversationCustomFieldsForView.map(
      (customField) => customField.id as ExploreColumnType
    );

    const sourceColumns =
      source === "conversations"
        ? [...visibleConversationColumns, ...conversationDynamicColumns]
        : [...visibleTicketColumns, ...ticketDynamicColumns];

    const uniqueSourceColumns = Array.from(new Set(sourceColumns));

    return uniqueSourceColumns
      .filter((column) => !mandatoryColumns.includes(column))
      .sort((a, b) => {
        const labelA = exploreColumnTypeToLabel[a] || a;
        const labelB = exploreColumnTypeToLabel[b] || b;
        return labelA.localeCompare(labelB);
      });
  }, [source, customFields, conversationCustomFieldsForView]);

  const availableOrderByColumns = useMemo(() => {
    if (value.groupBy) {
      const allAggOrderable = Array.from(
        new Set([...orderableMetricAggColumns])
      );

      if (source === "conversations") {
        return allAggOrderable.filter(
          (col) => col !== "avgQaScore" && col !== "avgAutoQaScore"
        );
      }
      return allAggOrderable;
    }

    if (source === "conversations") {
      return orderableColumns.filter(
        (column) => !["status", "avgQaScore"].includes(column)
      );
    }
    return orderableColumns;
  }, [source, value.groupBy]);

  const availableAggColumns = useMemo(() => {
    return source === "conversations"
      ? visibleConversationAggColumns
      : visibleTicketAggColumns;
  }, [source]);

  const handleGroupByChange = useCallback(
    (newGroupBy: ExploreGroupByColumnType | "none") => {
      const isGrouping = newGroupBy !== "none";

      if (isGrouping) {
        onChange({
          ...value,
          groupBy: newGroupBy,
          orderBy: undefined,
          orderDir: undefined,
          aggOrderBy: "volume",
          aggOrderDir: "desc",
          aggColumns: value.aggColumns?.length
            ? value.aggColumns
            : source === "conversations"
            ? defaultConversationsAggColumns
            : defaultTicketsAggColumns,
        });
      } else {
        onChange({
          ...value,
          groupBy: undefined,
          aggOrderBy: undefined,
          aggOrderDir: undefined,
          orderBy: "createdAt",
          orderDir: "desc",
          aggColumns: undefined,
        });
      }
    },
    [value, onChange, source]
  );

  const handleOrderByChange = useCallback(
    (newOrderBy: AllowedInteractionOrderBy | AllowedAggOrderBy) => {
      if (value.groupBy) {
        onChange({
          ...value,
          aggOrderBy: newOrderBy as AllowedAggOrderBy,
        });
      } else {
        onChange({
          ...value,
          orderBy: newOrderBy as AllowedInteractionOrderBy,
        });
      }
    },
    [value, onChange]
  );

  const handleOrderDirToggle = useCallback(() => {
    if (value.groupBy) {
      onChange({
        ...value,
        aggOrderDir: value.aggOrderDir === "asc" ? "desc" : "asc",
      });
    } else {
      onChange({
        ...value,
        orderDir: value.orderDir === "asc" ? "desc" : "asc",
      });
    }
  }, [value, onChange]);

  const handleColumnToggle = useCallback(
    (column: ExploreColumnType) => {
      if (mandatoryColumns.includes(column)) {
        return;
      }

      const defaultColumns =
        source === "conversations"
          ? defaultConversationsColumns
          : defaultTicketsColumns;

      const currentColumns =
        value.columns && value.columns.length > 0
          ? value.columns
          : defaultColumns;

      const newColumns = currentColumns.includes(column)
        ? currentColumns.filter((c) => c !== column)
        : [...currentColumns, column];

      const finalColumns = Array.from(
        new Set([...mandatoryColumns, ...newColumns])
      );

      onChange({
        ...value,
        columns: finalColumns,
      });
    },
    [value, onChange, source]
  );

  const handleAggColumnToggle = useCallback(
    (aggColumn: ExploreAggColumnType) => {
      const defaultAggColumns =
        source === "conversations"
          ? defaultConversationsAggColumns
          : defaultTicketsAggColumns;

      const currentAggColumns =
        value.aggColumns && value.aggColumns.length > 0
          ? value.aggColumns
          : defaultAggColumns;

      const newAggColumns = currentAggColumns.includes(aggColumn)
        ? currentAggColumns.filter((c) => c !== aggColumn)
        : [...currentAggColumns, aggColumn];

      onChange({
        ...value,
        aggColumns: newAggColumns.length > 0 ? newAggColumns : undefined,
      });
    },
    [value, onChange, source]
  );

  const selectedColumns = useMemo(() => {
    if (value.columns && value.columns.length > 0) {
      return Array.from(new Set(value.columns));
    }

    const defaultColumns =
      source === "conversations"
        ? defaultConversationsColumns
        : defaultTicketsColumns;
    const dynamicColumns =
      source === "conversations"
        ? conversationCustomFieldsForView.map(
            (customField) => customField.id as ExploreColumnType
          )
        : customFields.map(
            (customField) => customField.id as ExploreColumnType
          );

    return Array.from(new Set([...defaultColumns, ...dynamicColumns]));
  }, [value.columns, source, customFields, conversationCustomFieldsForView]);

  const selectedAggColumns = useMemo(() => {
    if (value.aggColumns && value.aggColumns.length > 0) {
      return value.aggColumns;
    }
    return source === "conversations"
      ? defaultConversationsAggColumns
      : defaultTicketsAggColumns;
  }, [value.aggColumns, source]);

  const activeSorting = useMemo(() => {
    if (value.groupBy) {
      return {
        orderBy: value.aggOrderBy,
        orderDir: value.aggOrderDir,
      };
    }
    return {
      orderBy: value.orderBy,
      orderDir: value.orderDir,
    };
  }, [
    value.groupBy,
    value.orderBy,
    value.orderDir,
    value.aggOrderBy,
    value.aggOrderDir,
  ]);

  const groupByLabel = useMemo(() => {
    if (!value.groupBy) return "No grouping";
    return exploreOrderByTypeToLabel[value.groupBy] || value.groupBy;
  }, [value.groupBy]);

  const orderByLabel = useMemo(() => {
    return (
      exploreOrderByTypeToLabel[
        activeSorting.orderBy as
          | AllowedInteractionOrderBy
          | AllowedAggOrderBy
          | ExploreGroupByColumnType
      ] || activeSorting.orderBy
    );
  }, [activeSorting.orderBy]);

  return {
    availableGroupByOptions,
    availableColumns,
    availableOrderByColumns,
    availableAggColumns,
    selectedAggColumns,
    activeSorting,
    groupByLabel,
    orderByLabel,
    handleGroupByChange,
    handleOrderByChange,
    handleOrderDirToggle,
    handleColumnToggle,
    handleAggColumnToggle,
  };
}
